import headerData from '../../Navigation/data/globalHeaderData.json';
import type { GlobalHeaderData } from '../../Navigation/data/types';
import type { SolutionsCatalogSnapshot } from '../../../shell/events/solutionsCatalogBridge';

/** runtime_agent.ukey → canvas module ukey (used when bridge module map is empty). */
export const FALLBACK_SOLUTION_CANVAS_MAP: Readonly<Record<string, string>> = {
  'daxl-data-prep-solution-agent': 'daxl-product-data-management',
  'daxl-harmonizer-solution-agent': 'daxl-product-data-management',
  'daxl-harmonizer-static-solution-agent': 'daxl-product-data-management',
  'daxl-data-onboarding-solution-agent': 'daxl-product-data-management',
  'daxl-data-onboarding-and-harmonization-solution-agent': 'daxl-product-data-management',
  'gaxl-ontology-builder-solution-agent': 'daxl-ontology-builder',
  'daxl-data-loading-solution-agent': 'daxl-product-data-management',
  'daxl-data-quality-solution-agent': 'daxl-product-data-management',
  'hcaxl-patient-risk-intelligence-solution-agent': 'hcaxl-patient-risk-intelligence',
  'daxl-entity-resolution-solution-agent': 'daxl-entity-resolution-solution',
};

const CANVAS_ROUTE_RE = /\/operational\/canvas\/(.+)$/;
const CANVAS_ROUTE_PREFIX = '/operational/canvas/';

export function canvasRouteForModule(moduleKey: string): string {
  return `${CANVAS_ROUTE_PREFIX}${moduleKey}`;
}

function getKnownModuleKeys(): string[] {
  const data = headerData as GlobalHeaderData;
  const keys: string[] = [];
  for (const app of data.applications) {
    for (const item of app.items) {
      if (item.type === 'module' && item.route) {
        const match = CANVAS_ROUTE_RE.exec(item.route);
        if (match) keys.push(match[1]);
      }
    }
  }
  return keys;
}

/**
 * Builds `runtime_agent.ukey -> canvas module` from the catalog bridge snapshot,
 * merging the hardcoded fallback so known agents (e.g. ontology builder) always
 * resolve even before moduleSolutionMap is complete.
 */
export function buildSolutionCanvasMap(
  snapshot: SolutionsCatalogSnapshot | null,
): Record<string, string> {
  if (!snapshot) return { ...FALLBACK_SOLUTION_CANVAS_MAP };

  const solutionToAgent = new Map<string, string>();
  for (const item of snapshot.catalogItems) {
    const agentUkey = item.runtime_agent?.ukey;
    const solUkey = item.solution?.ukey;
    if (agentUkey && solUkey && item.runtime_agent?.is_solution !== false) {
      solutionToAgent.set(solUkey, agentUkey);
    }
  }

  const moduleKeySet = new Set(getKnownModuleKeys());
  const dynamicMap: Record<string, string> = {};
  for (const [moduleKey, solutionUkeys] of Object.entries(snapshot.moduleSolutionMap)) {
    if (!moduleKeySet.has(moduleKey)) continue;
    for (const solUkey of solutionUkeys) {
      const agentUkey = solutionToAgent.get(solUkey);
      if (agentUkey) {
        dynamicMap[agentUkey] = moduleKey;
      }
    }
  }

  return { ...FALLBACK_SOLUTION_CANVAS_MAP, ...dynamicMap };
}
